import { Field, Text } from "@sitecore-jss/sitecore-jss-react";
import React, { useReducer } from "react";
import { useHistory } from "react-router-dom";

import { fetchDictionary } from "../../api/genericData";
import { fetchPolicy } from "../../api/policy";
import { BackButton } from "../../components/buttons/backButton/BackButton";
import { Button } from "../../components/buttons/Button";
import { Dropdown } from "../../components/dropdown/Dropdown";
import { ListWithHeader } from "../../components/lists/listWithHeader/ListWithHeader";
import { SearchLoader } from "../../components/loaders/searchLoader/SearchLoader";
import { useDidMount } from "../../hooks/useDidMount";
import { useQueryParams } from "../../hooks/useQueryParams";
import { PolicyDocuments } from "../policyDocuments/PolicyDocuments";
import { PolicyMutationType, QUERY_PARAM_MUTATION_TYPE } from "../policyMutation/PolicyMutation";
import { SectionWrapper } from "../sectionWrapper/SectionWrapper";
import { getInsuranceTakerLabels, getInsuredsLabels } from "./PolicyDetails.helpers";
import styles from "./PolicyDetails.module.scss";
import { initialState, policyDetailsReducer } from "./PolicyDetails.state";

interface DropDownMenuItem {
  id: PolicyMutationType;
  title: string;
}

export const DROPDOWN_MENU_ITEMS: DropDownMenuItem[] = [
  {
    id: "personalData",
    title: "Persoonsgegevens",
  },
  {
    id: "address",
    title: "Adres",
  },
  {
    id: "emailAddress",
    title: "E-mailadres",
  },
  {
    id: "phoneNumber",
    title: "Telefoonnummer",
  },
  {
    id: "bankAccount",
    title: "IBAN",
  },
  {
    id: "other",
    title: "Overig",
  },
];

interface Props {
  fields: Record<string, Field<string & { href: string }>>;
}

export const POLICY_NR_QUERY_PARAM = "policyNumber";
export const PRODUCT_QUERY_PARAM = "product";

export const PolicyDetails = ({ fields }: Props) => {
  const history = useHistory();
  const queryParams = useQueryParams();
  const policyNr = queryParams.get(POLICY_NR_QUERY_PARAM);

  const [data, dispatch] = useReducer(policyDetailsReducer, initialState);

  useDidMount(async () => {
    await retrievePolicy();
  });

  const retrievePolicy = async () => {
    if (!policyNr) {
      return;
    }

    try {
      dispatch({ type: "FETCH_DATA" });
      const { phrases } = await fetchDictionary();
      const policy = await fetchPolicy(policyNr);

      dispatch({ type: "FETCH_SUCCESS", payload: { data: { policy, dictionary: phrases } } });
    } catch {
      dispatch({ type: "FETCH_ERROR" });
    }
  };

  const handleItemClick = (index: number) => {
    const item = DROPDOWN_MENU_ITEMS[index];

    if (item) {
      const type = item.id;
      history.push(
        `${fields.AddressMutationPage.value.href}?${POLICY_NR_QUERY_PARAM}=${policyNr}&${QUERY_PARAM_MUTATION_TYPE}=${type}`
      );
    }
  };
  const renderContent = () => {
    if (data.policyDetails && data.policyDetailsHeader) {
      return (
        <SectionWrapper
          headerTitle={data.policyDetailsHeader.title}
          infoBlocks={[
            {
              label: "Product",
              content: data.policyDetailsHeader.productName,
            },
            {
              label: "Polisnummer",
              content: data.policyDetailsHeader.policyNumber,
            },
            {
              label: getInsuranceTakerLabels(data.policyDetailsHeader.insuranceTakers?.length ?? 0),
              content: data.policyDetailsHeader.insuranceTakers?.join("\n") || "",
            },
          ]}
        >
          <ListWithHeader
            columns={[
              {
                accessor: "key",
                Header: "Verzekering",
                flex: 2,
              },
              { accessor: "value", flex: 3 },
            ]}
            data={data.policyDetails}
            hideLastBorder
          />
          {data.policyInsuredsData && (
            <>
              <ListWithHeader
                columns={[
                  { accessor: "insured", Header: getInsuredsLabels(data.policyInsuredsData.insureds.length) },
                  { accessor: "insuranceType", Header: "Type uitvaartverzekering" },
                  { accessor: "insuredAmount", Header: "Hoogte verzekerd kapitaal" },
                  { accessor: "premiumPaymentTerm", Header: "Betaalduur" },
                  { accessor: "premium", Header: "Premie" },
                ]}
                data={data.policyInsuredsData.insureds}
              />

              <div className={styles.policyDetails__premiumWrapper}>
                <div className={styles.policyDetails__premiumContainerLeft} />
                <div className={styles.policyDetails__premiumContainerRight}>
                  <div>
                    <p>Totaal</p>
                  </div>
                  <div>
                    <p>{data.policyInsuredsData.totalPremium}</p>
                  </div>
                </div>
              </div>
            </>
          )}
          {data.policyCoverageData && (
            <>
              {data.policyCoverageData.covers.map((coverage, index) => (
                <ListWithHeader
                  key={`policyCoverageData${index}`}
                  columns={[
                    { accessor: "key", Header: `Dekking ${index + 1}` },
                    { accessor: "value", flex: 0.82 },
                  ]}
                  data={coverage.coverageData}
                />
              ))}
              <div className={styles.policyDetails__coveragePremium}>
                <div className={styles.policyDetails__coveragePremiumContainerLeft}>
                  <p>{data.policyCoverageData.paymentTerm}</p>
                </div>
                <div className={styles.policyDetails__coveragePremiumContainerRight}>
                  <p>{data.policyCoverageData.totalPremium}</p>
                </div>
              </div>
            </>
          )}
          {data.productCode === "DCP" && <PolicyDocuments policyNumber={policyNr} productCode={null} />}
        </SectionWrapper>
      );
    }

    const message = data.loading ? fields.LoadingMessage.value : fields.ErrorMessage.value;

    return <SearchLoader text={message} />;
  };

  return (
    <div className={styles.policyDetails__wrapper}>
      <div className={styles.policyDetails__header}>
        <div className={styles.policyDetails__buttonsLeft}>
          <BackButton>
            <Text field={fields.BackButton} />
          </BackButton>
        </div>
        <div className={styles.policyDetails__buttonsRight}>
          <Button
            leading={<i className={`icon icon-download ${styles.policyDetails__printIcon}`} />}
            disabled={data.loading || !data.policyDetails}
            onClick={() => window.print()}
          >
            PDF Download
          </Button>
          <Dropdown
            onItemClick={handleItemClick}
            items={DROPDOWN_MENU_ITEMS}
            buttonProps={{
              id: "button_edit_policy",
              type: "primary",
              disabled: !data.canEdit || data.loading || !data.policyDetails,
              children: "Polis wijzigen",
            }}
          />
        </div>
      </div>
      {renderContent()}
    </div>
  );
};
